package com.nonopn.skydome.common.app.pages.documents

import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryType
import com.nonopn.skydome.common.app.pages.probes.matchesSearch

// Pure helpers of the "Reports & Certificates" screen: grouping by site, year filter and the
// merge between the local cache and the history stored on the server. No UI nor network here.

const val DocumentsPerRequest = 100

private val IdPrefixes = listOf("keraunic_client_", "cert_nsg_client_", "cert_foudr_client_")

/**
 * A history item id is built as `<prefix><projectUuid>_<epochMillis>`: extract the project uuid so that a
 * document can be reopened for its project. Falls back to the whole id for unknown formats.
 */
fun DocumentHistoryItem.projectUuid(): String {
    val prefix = IdPrefixes.firstOrNull { id.startsWith(it) } ?: return id
    val withoutPrefix = id.removePrefix(prefix)
    val separator = withoutPrefix.lastIndexOf('_')
    val suffix = if (separator >= 0) withoutPrefix.substring(separator + 1) else ""
    return if (separator > 0 && suffix.isNotEmpty() && suffix.all { it.isDigit() }) {
        withoutPrefix.substring(0, separator)
    } else {
        withoutPrefix
    }
}

/** Identity of a site: its project uuid, or name + coordinates for items without a known project. */
fun DocumentHistoryItem.siteKey(): String {
    val uuid = projectUuid()
    return if (uuid != id) {
        uuid
    } else {
        "${siteName.trim().lowercase()}|${latitude ?: ""}|${longitude ?: ""}"
    }
}

/** Documents of one site, newest first. The site name and position are those of the newest document. */
data class SiteDocuments(
    val key: String,
    val siteName: String,
    val projectUuid: String,
    val latitude: Double?,
    val longitude: Double?,
    val documents: List<DocumentHistoryItem>,
) {
    val latestAtSecondsUtc: Long get() = documents.firstOrNull()?.createdAtSecondsUtc ?: 0L
}

/** Years (newest first) having at least one document of the given type. */
fun availableYears(
    history: List<DocumentHistoryItem>,
    type: DocumentHistoryType,
    yearOf: (Long) -> Int,
): List<Int> = history
    .filter { it.type == type }
    .map { yearOf(it.createdAtSecondsUtc) }
    .distinct()
    .sortedDescending()

/**
 * Documents of [type] grouped by site: sites sorted by their most recent document, documents sorted newest first.
 * [query] filters on the site name, [year] (when not null) on the creation year.
 */
fun groupDocumentsBySite(
    history: List<DocumentHistoryItem>,
    type: DocumentHistoryType,
    query: String,
    year: Int?,
    yearOf: (Long) -> Int,
): List<SiteDocuments> = history
    .asSequence()
    .filter { it.type == type }
    .filter { query.isBlank() || matchesSearch(query, it.siteName) }
    .filter { year == null || yearOf(it.createdAtSecondsUtc) == year }
    .groupBy { it.siteKey() }
    .map { (key, items) ->
        val documents = items.sortedWith(
            compareByDescending<DocumentHistoryItem> { it.createdAtSecondsUtc }.thenBy { it.id }
        )
        val newest = documents.first()
        SiteDocuments(
            key = key,
            siteName = newest.siteName,
            projectUuid = newest.projectUuid(),
            latitude = newest.latitude,
            longitude = newest.longitude,
            documents = documents,
        )
    }
    .sortedWith(compareByDescending<SiteDocuments> { it.latestAtSecondsUtc }.thenBy { it.key })
    .toList()

private fun DocumentHistoryItem.identity() = "${type.name}|$id"

/**
 * Union of the given histories, newest first. An item present several times (same type and id) is kept once,
 * the most recent one winning; on equal dates the later list wins.
 */
fun mergeDocumentHistories(vararg histories: List<DocumentHistoryItem>): List<DocumentHistoryItem> {
    val byIdentity = LinkedHashMap<String, DocumentHistoryItem>()
    histories.forEach { list ->
        list.forEach { item ->
            val existing = byIdentity[item.identity()]
            if (existing == null || item.createdAtSecondsUtc >= existing.createdAtSecondsUtc) {
                byIdentity[item.identity()] = item
            }
        }
    }
    return byIdentity.values.sortedWith(
        compareByDescending<DocumentHistoryItem> { it.createdAtSecondsUtc }.thenBy { it.id }
    )
}

/** Local items the server does not know yet: they are sent to it (migration and retry of failed uploads). */
fun missingOnServer(
    local: List<DocumentHistoryItem>,
    server: List<DocumentHistoryItem>,
): List<DocumentHistoryItem> {
    val known = server.map { it.identity() }.toHashSet()
    return local.filterNot { known.contains(it.identity()) }
}
